import { z } from "zod"
import {
  NOTIFICATION_EVENT_ENUM,
  NotificationPreferencesSchema,
  NotificationEventPreferenceSchema,
  type GetNotificationPreferencesResponseType,
} from "@antipartido-inc/contracts-ifaces"
import { type NotificationEventType } from "./NotificationEventType.js"

export interface PreferenceItem {
  readonly eventType: NotificationEventType
  readonly channels: {
    readonly email: boolean
    readonly inApp: boolean
  }
}

const PreferenceItemValidation = z.object({
  eventType: z.nativeEnum(NOTIFICATION_EVENT_ENUM, { required_error: "eventType is required" }),
  channels: z.object({
    email: z.boolean({ required_error: "email is required" }),
    inApp: z.boolean({ required_error: "inApp is required" }),
  }),
})

const NotificationPreferencesValidation = z.object({
  preferences: z.array(PreferenceItemValidation),
})

export class NotificationPreferences {
  constructor(private readonly _preferences: readonly PreferenceItem[]) {}

  static create({ preferences }: z.infer<typeof NotificationPreferencesValidation>) {
    const { success, error } = NotificationPreferencesValidation.safeParse({ preferences })
    if (!success) throw new Error(error.message)
    return new NotificationPreferences(preferences)
  }

  static createFromAPI(data: GetNotificationPreferencesResponseType) {
    const { success: apiSuccess, error: apiError } = NotificationPreferencesSchema.safeParse(data)
    if (!apiSuccess) throw new Error(apiError.message)

    const preferences = data.preferences.map((p) => {
      const parsed = NotificationEventPreferenceSchema.safeParse({
        eventType: p.eventType,
        channels: { email: p.channels.email, inApp: p.channels.inApp },
      })
      if (!parsed.success) throw new Error(parsed.error.message)
      return parsed.data
    })
    return NotificationPreferences.create({ preferences })
  }

  get preferences() { return this._preferences } // prettier-ignore

  getByEventType(eventType: NotificationEventType): PreferenceItem | undefined {
    return this._preferences.find((p) => p.eventType === eventType)
  }

  serialize() {
    return {
      preferences: this.preferences.map((p) => ({
        eventType: p.eventType,
        channels: { email: p.channels.email, inApp: p.channels.inApp },
      })),
    }
  }
}

export type NotificationChannelKey = keyof PreferenceItem["channels"]

export type NotificationPreferencesSerialized = ReturnType<NotificationPreferences["serialize"]>
